/**
 * Sheet Manager
 *
 * Handles opening and closing of header sheets.
 * Also supports opening sheets programmatically via the hero search trigger.
 */

document.addEventListener('DOMContentLoaded', () => {
  // Portal pass: any sheet with [data-portal-to="<selector>"] gets
  // moved into that container before SheetManager binds. Keeps sheet
  // markup co-located with its source template (e.g. AllyRewardSingle)
  // while letting it inherit positioning from the floating header's
  // .header-dropdowns-container, so its absolute top:0 anchors right
  // under the masthead instead of leaving a 20-30px gap below.
  document.querySelectorAll('[data-portal-to]').forEach((node) => {
    const target = document.querySelector(node.getAttribute('data-portal-to'));
    if (target && !target.contains(node)) {
      target.appendChild(node);
    }
  });

  class SheetManager {
    constructor() {
      this.activeSheet = null;
      this.overlay = document.getElementById('sheetOverlay');
      this._openedThisTick = false;

      this.init();
    }

    init() {
      // Global delegated listener for elements with [data-sheet-target] (handles dynamic/PJAX elements)
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-sheet-target]');
        if (trigger) {
          const href = trigger.getAttribute('href');
          if (!href || href === '#') {
            e.preventDefault();
          }
          e.stopPropagation();

          const targetId = trigger.getAttribute('data-sheet-target');
          this.toggle(targetId, trigger);
        }
      });

      // Hero search trigger — opens search sheet
      document.querySelectorAll('.bfwt-js-search-trigger').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const searchSheet = document.getElementById('searchSheet');
          const trigger = document.querySelector(
            '[data-sheet-target="searchSheet"]'
          );
          if (searchSheet && trigger) {
            this.open(searchSheet, trigger);
          }
        });
      });

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeAll());
      }

      // Escape key + Cmd/Ctrl+B search shortcut
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAll();
          return;
        }
        if ((e.metaKey || e.ctrlKey) && e.key && e.key.toLowerCase() === 'b') {
          e.preventDefault();
          const searchTrigger = document.querySelector(
            '[data-sheet-target="searchSheet"]'
          );
          this.toggle('searchSheet', searchTrigger);
        }
      });

      // Close button + outside click
      document.addEventListener('click', (e) => {
        // If sheet was opened in the current event loop tick, ignore outside clicks to prevent instant closing
        if (this._openedThisTick) {
          return;
        }

        // Close button (data-sheet-close attribute or legacy class names)
        if (
          e.target.closest('[data-sheet-close]') ||
          e.target.closest('.bfwt-btn-close') ||
          e.target.closest('.bfwt-sheet-close') ||
          e.target.closest('.bfwt-sheet__close')
        ) {
          this.closeAll();
          return;
        }

        // If the clicked element is no longer in the DOM, it was likely removed/replaced
        // by a click handler inside the sheet (e.g. innerHTML updates on buttons or chips).
        // We should not treat this as an outside click.
        if (e.target instanceof Node && !document.body.contains(e.target)) {
          return;
        }

        // Outside click — but NOT inside sheets or triggers
        // Note: We check specifically for Is Active sheets to be sure
        if (
          this.activeSheet &&
          e.target &&
          typeof e.target.closest === 'function' &&
          !e.target.closest('.bfwt-floating-sheet') &&
          !e.target.closest('[data-sheet-target]') &&
          !e.target.closest('.bfwt-js-search-trigger')
        ) {
          this.closeAll();
        }
      });

      // Resize listener for active sheet height adjustment on mobile
      const handleResize = () => {
        if (this.activeSheet) {
          this.updateMobileHeight();
        }
      };
      window.addEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
      }

      // Recalculate sheet heights when items are clicked inside active sheet
      document.addEventListener('click', (e) => {
        if (this.activeSheet && e.target instanceof Node && this.activeSheet.contains(e.target)) {
          setTimeout(() => {
            this.updateMobileHeight();
          }, 150);
        }
      });

      // Listen for mobile virtual keyboard open/close via visualViewport resize
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
          if (this.activeSheet && window.innerWidth < 768) {
            this.updateMobileHeight();
          }
        });
      }

      // Initialize sheet tabs and mobile navigation
      this.initSearchSheetDirectory();
      this.initExploreSheetDirectory();
      this.initReviewsSheetDirectory();
      this.initSavedSheetDirectory();
    }

    initSearchSheetDirectory() {
      const sheet = document.getElementById('searchSheet');
      if (!sheet) return;

      window.bfwtUpdateMobileHeight = () => {
        if (window.innerWidth >= 768) {
          sheet.style.height = '';
          return;
        }

        const header = sheet.querySelector('.bfwt-search-header-row');
        const filters = sheet.querySelector('#bfwt-search-filters');
        const idleBody = sheet.querySelector('#bfwt-search-idle-body');
        const discoveryStrip = sheet.querySelector(
          '#bfwt-search-discovery-strip'
        );
        const recentBrands = sheet.querySelector('#bfwt-search-recent-brands');
        const quickJump = sheet.querySelector('#bfwt-search-directory');
        const results = sheet.querySelector('#bfwt-modal-results');
        const footer = sheet.querySelector('.bfwt-search-footer-row');

        const vh = window.visualViewport
          ? window.visualViewport.height
          : window.innerHeight;
        const rect = sheet.getBoundingClientRect();
        const topOffset = rect && rect.top > 0 ? rect.top : 56;
        const maxAllowedHeight = Math.max(260, vh - topOffset - 16);

        // When actively showing search results, give results full available viewport height
        if (results && !results.classList.contains('tw-hidden')) {
          sheet.style.height = maxAllowedHeight + 'px';
          return;
        }

        const headerHeight = header ? header.offsetHeight : 56;
        const footerHeight = footer ? footer.offsetHeight : 42;
        const filtersHeight =
          filters && !filters.classList.contains('tw-hidden')
            ? filters.offsetHeight
            : 0;
        const discoveryHeight =
          discoveryStrip && !discoveryStrip.classList.contains('tw-hidden')
            ? discoveryStrip.scrollHeight
            : 0;
        const recentBrandsHeight =
          recentBrands && !recentBrands.classList.contains('tw-hidden')
            ? recentBrands.scrollHeight
            : 0;
        const quickJumpHeight =
          quickJump && !quickJump.classList.contains('tw-hidden')
            ? quickJump.scrollHeight
            : 0;
        const idleContentHeight = Math.max(
          discoveryHeight + recentBrandsHeight + quickJumpHeight,
          idleBody && !idleBody.classList.contains('tw-hidden')
            ? idleBody.scrollHeight
            : 0
        );

        const targetHeight = Math.min(
          headerHeight +
            filtersHeight +
            idleContentHeight +
            footerHeight +
            8,
          maxAllowedHeight
        );

        sheet.style.height = targetHeight + 'px';
      };
    }

    initExploreSheetDirectory() {
      const sheet = document.getElementById('sheet-explore');
      if (!sheet) return;

      const tabButtons = sheet.querySelectorAll('.bfwt-find-deals-tab-btn');
      const panels = sheet.querySelectorAll('.bfwt-find-deals-panel');

      tabButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetId = btn.getAttribute('data-target-panel');

          tabButtons.forEach((b) => {
            b.classList.remove(
              'is-active',
              'tw-bg-ink/[0.06]',
              'dark:tw-bg-white/[0.08]',
              'tw-text-ink',
              'tw-border-line/80',
              'tw-bg-accent/[0.08]',
              'tw-text-accent',
              'tw-border-accent/40'
            );
            b.classList.add(
              'tw-bg-transparent',
              'tw-text-secondary',
              'tw-border-transparent'
            );
          });
          btn.classList.add(
            'is-active',
            'tw-bg-ink/[0.06]',
            'dark:tw-bg-white/[0.08]',
            'tw-text-ink',
            'tw-border-line/80'
          );
          btn.classList.remove(
            'tw-bg-transparent',
            'tw-text-secondary',
            'tw-border-transparent'
          );

          panels.forEach((p) => p.classList.add('tw-hidden'));
          const activePanel = document.getElementById(targetId);
          if (activePanel) {
            activePanel.classList.remove('tw-hidden');
          }

          if (window.innerWidth < 768) {
            try {
              btn.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
              });
            } catch (_) {}
          }

          this.updateMobileHeight();
        });
      });
    }

    initSavedSheetDirectory() {
      // Vault sheet uses direct 4-card hub navigation; no nested tabs required
    }

    initReviewsSheetDirectory() {
      const sheet = document.getElementById('sheet-reviews');
      if (!sheet) return;

      const tabButtons = sheet.querySelectorAll('.bfwt-read-tab-btn');
      const panels = sheet.querySelectorAll('.bfwt-reviews-panel');

      tabButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetId = btn.getAttribute('data-target-panel');

          tabButtons.forEach((b) => {
            b.classList.remove(
              'is-active',
              'tw-bg-ink/[0.06]',
              'dark:tw-bg-white/[0.08]',
              'tw-text-ink',
              'tw-border-line/80'
            );
            b.classList.add(
              'tw-bg-transparent',
              'tw-text-secondary',
              'tw-border-transparent'
            );
          });
          btn.classList.add(
            'is-active',
            'tw-bg-ink/[0.06]',
            'dark:tw-bg-white/[0.08]',
            'tw-text-ink',
            'tw-border-line/80'
          );
          btn.classList.remove(
            'tw-bg-transparent',
            'tw-text-secondary',
            'tw-border-transparent'
          );

          panels.forEach((p) => {
            p.classList.remove('tw-flex');
            p.classList.add('tw-hidden');
          });
          const activePanel = document.getElementById(targetId);
          if (activePanel) {
            activePanel.classList.remove('tw-hidden');
            activePanel.classList.add('tw-flex');
          }

          if (window.innerWidth < 768) {
            try {
              btn.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
              });
            } catch (_) {}
          }

          this.updateMobileHeight();
        });
      });
    }

    toggle(sheetId, trigger) {
      const sheet = document.getElementById(sheetId);
      if (!sheet) {
        return;
      }

      // Check if THIS sheet is currently active (using class check for robustness)
      if (sheet.classList.contains('is-active')) {
        this.closeAll();
      } else {
        this.open(sheet, trigger);
      }
    }

    open(sheet, trigger) {
      // Close all other sheets first (but keep overlay active for smooth transition)
      this.closeAll(true);

      this.activeSheet = sheet;
      this._lastSheetTrigger = trigger || this._lastSheetTrigger;

      // Prevent outside click handler from immediately closing sheet on the current click event
      this._openedThisTick = true;
      setTimeout(() => {
        this._openedThisTick = false;
      }, 0);

      // GA / dataLayer: sheet_open or search_open
      if (typeof window.bfwt_trackEvent === 'function') {
        if (sheet.id === 'searchSheet') {
          const source =
            trigger && trigger.classList.contains('bfwt-js-search-trigger')
              ? 'hero'
              : 'header';
          window.bfwt_trackEvent('search_open', {
            source: source,
            event_category: 'search',
          });
        } else if (sheet.id !== 'sheet-newsletter') {
          window.bfwt_trackEvent('sheet_open', {
            sheet_id: sheet.id,
            event_category: 'navigation',
          });
        }
      }

      // [FIX] iOS Critical: Temporarily disable transitions and force visibility.
      sheet.style.transition = 'none';
      sheet.style.opacity = '1';
      sheet.style.visibility = 'visible';

      sheet.classList.add('is-active');
      sheet.setAttribute('aria-hidden', 'false'); // Show new sheet
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      if (this.overlay) {
        this.overlay.classList.add('is-active');
      }

      document.body.classList.add('bfwt-sheet-open');

      // Update mobile height
      setTimeout(() => {
        this.updateMobileHeight();
      }, 60);

      // Auto-focus input ONLY on Desktop (Search Sheet / Account Sheet) or Newsletter Sheet
      // so mobile sheets (including Search on mobile) never auto-pop the virtual keyboard and cut the sheet in half!
      let focusTarget = null;
      if (sheet.id === 'searchSheet' && window.innerWidth >= 768) {
        focusTarget = sheet.querySelector('#bfwt-modal-input');
      } else if (sheet.id === 'sheet-newsletter' && window.innerWidth >= 768) {
        focusTarget =
          sheet.querySelector('#bfwt-sheet-email') ||
          sheet.querySelector('input[type="email"]');
      } else if (sheet.id === 'accountSheet' && window.innerWidth >= 768) {
        const loggedOut = document.getElementById(
          'bfwt-account-logged-out-view'
        );
        if (loggedOut && !loggedOut.classList.contains('tw-hidden')) {
          focusTarget = loggedOut.querySelector(
            'form:not(.tw-hidden) input[type="email"], form:not(.tw-hidden) input[type="text"]:not([readonly])'
          );
        }
      }

      if (focusTarget) {
        // Focus Operation (Synchronous)
        focusTarget.focus({ preventScroll: true });

        // iOS Selection Range Hack
        if (focusTarget.setSelectionRange && focusTarget.type !== 'email') {
          try {
            const len = focusTarget.value.length;
            focusTarget.setSelectionRange(len, len);
          } catch (e) {
            /* ignore */
          }
        }

        // [Backup] Ensure focus sticks after any potential layout shifts/scripts
        setTimeout(() => {
          focusTarget.focus({ preventScroll: true });
        }, 100);
      }

      // Always restore transitions later
      // This is CRITICAL: If we don't clear these inline styles, sheets without inputs (Explore, Daily Drop)
      // will stay permanently visible regardless of class removal.
      setTimeout(() => {
        sheet.style.transition = '';
        sheet.style.opacity = '';
        sheet.style.visibility = '';
      }, 50);

      // Lazy Load Turnstile (Newsletter Only)
      if (sheet.id === 'sheet-newsletter' && window.loadTurnstile) {
        window.loadTurnstile();
      }
    }

    closeAll(keepOverlay = false) {
      // Find ANY sheet with stuck inline visibility, just in case
      // The selector finds any sheet that might have inline styles set
      const allSheets = document.querySelectorAll('.bfwt-floating-sheet');

      allSheets.forEach((sheet) => {
        // Prevent ARIA warning: Blur focus if inside the sheet being hidden
        if (sheet.contains(document.activeElement)) {
          document.activeElement.blur();
        }

        // Clear state
        sheet.classList.remove('is-active');
        sheet.setAttribute('aria-hidden', 'true'); // Hide sheet

        // Clear stuck inline styles (critical for fix)
        sheet.style.opacity = '';
        sheet.style.visibility = '';
        sheet.style.transition = '';

        // Reset trigger aria state
        if (sheet.id) {
          const trigger = document.querySelector(
            `[data-sheet-target="${sheet.id}"]`
          );
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
          }
        }
      });

      this.activeSheet = null;

      // Only close overlay/body if explicitly requested (default)
      // When switching sheets (open calling closeAll), keepOverlay is true.
      if (!keepOverlay) {
        if (this.overlay) {
          this.overlay.classList.remove('is-active');
        }
        document.body.classList.remove('bfwt-sheet-open');

        // Return focus to the trigger that opened the sheet (accessibility)
        if (
          this._lastSheetTrigger &&
          typeof this._lastSheetTrigger.focus === 'function'
        ) {
          try {
            this._lastSheetTrigger.focus();
          } catch (_) {}
        }
        this._lastSheetTrigger = null;
      }
    }

    updateMobileHeight() {
      const sheet = this.activeSheet;
      if (!sheet) return;

      if (window.innerWidth >= 768) {
        sheet.style.height = '';
        return;
      }

      const getViewportMaxHeight = (targetSheet) => {
        const vh = window.visualViewport
          ? window.visualViewport.height
          : window.innerHeight;
        const rect = targetSheet ? targetSheet.getBoundingClientRect() : null;
        const top = rect && rect.top > 0 ? rect.top : 56;
        return Math.max(260, vh - top - 16);
      };

      const maxAllowedHeight = getViewportMaxHeight(sheet);

      // 1. searchSheet height logic delegator
      if (sheet.id === 'searchSheet') {
        if (typeof window.bfwtUpdateMobileHeight === 'function') {
          window.bfwtUpdateMobileHeight();
        }
        return;
      }

      // 2. sheet-explore height logic (Header + Mobile Pill Strip + Active Panel + Footer)
      if (sheet.id === 'sheet-explore') {
        const header = sheet.querySelector('.bfwt-explore-header-row');
        const pillBar = sheet.querySelector('.bfwt-explore-sidebar');
        const panels = sheet.querySelectorAll('.bfwt-find-deals-panel');
        const promoFooter = sheet.querySelector(
          'div.tw-flex-none.tw-flex.tw-items-center.tw-justify-between'
        );

        const headerHeight = header ? header.offsetHeight : 52;
        const pillBarHeight = pillBar ? pillBar.offsetHeight : 48;
        const footerHeight =
          promoFooter && promoFooter.offsetHeight
            ? promoFooter.offsetHeight
            : 0;
        const activePanel = Array.from(panels).find(
          (p) => p && !p.classList.contains('tw-hidden')
        );
        const contentHeight = activePanel ? activePanel.scrollHeight + 28 : 340;

        sheet.style.height =
          Math.min(
            headerHeight + pillBarHeight + contentHeight + footerHeight,
            maxAllowedHeight
          ) + 'px';
        return;
      }

      // 3. accountSheet height logic
      if (sheet.id === 'accountSheet') {
        const loggedOutView = document.getElementById(
          'bfwt-account-logged-out-view'
        );
        const loggedInView = document.getElementById(
          'bfwt-account-logged-in-view'
        );

        if (loggedOutView && !loggedOutView.classList.contains('tw-hidden')) {
          const header = loggedOutView.querySelector('div:first-child');
          const formsContainer = loggedOutView.querySelector('.tw-flex-1');
          const headerHeight = header ? header.offsetHeight : 48;
          const contentHeight = formsContainer
            ? formsContainer.scrollHeight + 16
            : 440;

          sheet.style.height =
            Math.min(headerHeight + contentHeight, maxAllowedHeight) + 'px';
          return;
        }

        if (loggedInView && !loggedInView.classList.contains('tw-hidden')) {
          const header = loggedInView.querySelector('div:first-child');
          const pillBar = loggedInView.querySelector(
            '.bfwt-account-sidebar-el'
          );
          const panels = loggedInView.querySelectorAll('.bfwt-account-panel');
          const appPromo = loggedInView.querySelector(
            'div.tw-flex-none.tw-flex.tw-items-center.tw-justify-between'
          );
          const logoutBtnRow = loggedInView.querySelector('div.md\\:tw-hidden');

          const headerHeight = header ? header.offsetHeight : 52;
          const pillBarHeight = pillBar ? pillBar.offsetHeight : 48;
          const appPromoHeight =
            appPromo && appPromo.offsetHeight ? appPromo.offsetHeight : 0;
          const logoutBtnHeight =
            logoutBtnRow && logoutBtnRow.offsetHeight
              ? logoutBtnRow.offsetHeight
              : 0;
          const activePanel = Array.from(panels).find(
            (p) => p && !p.classList.contains('tw-hidden')
          );
          const contentHeight = activePanel
            ? activePanel.scrollHeight + 28
            : 360;

          sheet.style.height =
            Math.min(
              headerHeight +
                pillBarHeight +
                contentHeight +
                appPromoHeight +
                logoutBtnHeight,
              maxAllowedHeight
            ) + 'px';
          return;
        }
      }

      // 4. sheet-saved (Your Vault) height logic
      if (sheet.id === 'sheet-saved') {
        const guestView = document.getElementById('bf-saved-guest-view');
        const memberView = document.getElementById('bf-saved-member-view');
        const header = sheet.querySelector('.bfwt-saved-header-row');
        const headerHeight = header ? header.offsetHeight : 52;
        const appPromo = sheet.querySelector(
          'div.tw-flex-none.tw-flex.tw-items-center.tw-justify-between'
        );
        const appPromoHeight =
          appPromo && appPromo.offsetHeight ? appPromo.offsetHeight : 0;

        if (guestView && !guestView.classList.contains('tw-hidden')) {
          const contentHeight = guestView.scrollHeight + 16;
          sheet.style.height =
            Math.min(
              headerHeight + contentHeight + appPromoHeight,
              maxAllowedHeight
            ) + 'px';
          return;
        }
        if (memberView && !memberView.classList.contains('tw-hidden')) {
          const contentHeight = memberView.scrollHeight + 16;
          sheet.style.height =
            Math.min(
              headerHeight + contentHeight + appPromoHeight,
              maxAllowedHeight
            ) + 'px';
          return;
        }
      }

      // 5. sheet-reviews (The Daily Read) height logic
      if (sheet.id === 'sheet-reviews') {
        const header = sheet.querySelector('.bfwt-reviews-header-row');
        const pillBar = sheet.querySelector('.bfwt-reviews-mobile-tabs');
        const directory = sheet.querySelector('#bfwt-reviews-directory');
        const appPromo = sheet.querySelector(
          'div.tw-flex-none.tw-flex.tw-items-center.tw-justify-between'
        );

        const headerHeight = header ? header.offsetHeight : 52;
        const pillBarHeight = pillBar ? pillBar.offsetHeight : 44;
        const appPromoHeight =
          appPromo && appPromo.offsetHeight ? appPromo.offsetHeight : 0;
        const contentHeight = directory ? directory.scrollHeight + 16 : 360;

        sheet.style.height =
          Math.min(
            headerHeight + pillBarHeight + contentHeight + appPromoHeight,
            maxAllowedHeight
          ) + 'px';
        return;
      }

      // 6. Default / Fallback height calculation
      sheet.style.height =
        Math.min(sheet.scrollHeight, maxAllowedHeight) + 'px';
    }
  }

  window.sheetManager = new SheetManager();
});
